import React, { useState, useEffect, useRef } from 'react';
import { Button, Dialog, DialogTitle, DialogContent, 
        DialogActions, IconButton, TextField, Typography, Box } from '@mui/material';
import { useConfig, configKeys, serverUrl } from './ConfigHolder';
import { Close as CloseIcon, Mic as MicIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { isMobile } from 'react-device-detect';

import { post_auth } from './auth';
import { useAuth } from './AuthProvider';
import { isRTL } from './generalUtils';
import ErrorDispatcher from './ErrorDispatcher';


const translateText = async (text, target_language, source_language, logout, throwExp = false) => {
    const data = {
        text,
        target_language,
        source_language,
    };
    const uri = `${serverUrl}/translate`;

    try {
        const response = await post_auth(uri, data, logout);
        console.log('received translate ', response.data);
        return response.data.translatedText;
    } catch (error) {
        console.error(error);
        if (throwExp) {
            throw error;
        }

        return null;
    }
};

const FormTranslate = ({ isModalOpen, inputText, inputTextCallBack, onClose, errorCallBack }) => {
    const [translatedText, setTranslatedText] = useState('');
    const [toTranslate, setToTranslate] = useState('');
    const { config } = useConfig();
    const langLearn = config[configKeys.langLearn];
    const langBase = config[configKeys.langBase];
    const [listening, setListening] = useState(false);
    const recognitionRef = useRef(null);
    const { logout } = useAuth();
    const [error, setError] = useState(null);
    const [updateTranslateVar, setUpdateTranslateVar] = useState(0);
    
    // const isIOS = () => {
    //     return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    // };
    const speechRecSupported = ('webkitSpeechRecognition' in window) && !isMobile;
    
    const [micReleased, setMicReleased] = useState(true);

    const handleMicClick = () => {
        if (!('webkitSpeechRecognition' in window)) {
            alert("Speech recognition is not supported in this browser.");
            return;
        }

        const recognition = new window.webkitSpeechRecognition();
        recognitionRef.current = recognition;
        recognition.lang = langBase.value;
        recognition.continuous = false;
        recognition.interimResults = false;

        recognition.onstart = () => {
            setListening(true);
            setMicReleased(false);
        };
        recognition.onend = async () => {
            setListening(false);
            await fullCleanup(); // Full cleanup after recognition ends
        };
        recognition.onerror = (event) => {
            console.error('Speech recognition error:', event);
            setListening(false);
            fullCleanup();
        };

        recognition.onresult = (event) => {
            const transcript = event.results[0][0].transcript;
            console.log(event.results)
            setToTranslate((prev) => prev + ' ' + transcript);
        };

        recognition.start();
    };

    const cleanupRecognition = () => {
        if (recognitionRef.current) {
            recognitionRef.current.onend = null;
            recognitionRef.current.stop();
            recognitionRef.current.onerror = null;
            recognitionRef.current.onresult = null;
            recognitionRef.current = null;
        }
    };

    const releaseMicrophone = async () => {
        if (micReleased)  return;
        console.log ('Releasing the mic in FormTranslate')
        try {
            let stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            stream.getTracks().forEach(track => {
                try {
                    track.stop();
                } catch (error) {
                    console.error('Error stopping track:', error);
                }
            });
            stream = null;
            setMicReleased(true);
        } catch (error) {
            console.error('Error accessing microphone for release:', error);
        }
    };

    // const releaseAudioContext = () => {
    //     try {
    //         const audioContext = new window.AudioContext();
    //         audioContext.close();
    //     } catch (error) {
    //         console.error('Error closing AudioContext:', error);
    //     }
    // };

    const fullCleanup = async () => {
        cleanupRecognition();
        await releaseMicrophone();
    };

    const handleClose = async () => {
        if (recognitionRef.current) {
            recognitionRef.current.stop();
        }
        await fullCleanup();
        // releaseAudioContext();
        onClose();
    };

    useEffect(() => {
        inputTextCallBack(translatedText);
    // eslint-disable-next-line
    }, [translatedText, updateTranslateVar]);

    useEffect(() => {
        if (isModalOpen) {
            translateToBase();
        }
        return () => {
            fullCleanup();
        };
        // eslint-disable-next-line
    }, [isModalOpen]);

    const translateToBase = async () => {
        if (!inputText || inputText.length === 0) {
            setToTranslate('');
            return;
        }
        if (!langBase.value || !langLearn.value) {
            console.error('Invalid language values');
            return;
        }
        try {
            const res = await translateText(inputText, langBase.value, langLearn.value, logout, true);
            if (res) {
                console.log('Received translated to Base: ', res);
                setToTranslate(res);
            } else {
                console.error('Translation failed');
            }
        }
        catch (error) {
            console.error('Error during translation:', error);
            if (errorCallBack) { errorCallBack(error); } else 
                {
                    setError(error);
                }
        }
    };

    const handleInputChange = (e) => {
        setToTranslate(e.target.value);
    };

    const handleTranslateClick = async () => {
        // setTranslatedText(""); //this is an alternative to using updateTranslateVar. I am not sure which one is better. 
        // But I think that using above triggers 2 updates in useEffect instead of one. 2025-05-23 MB
        try {
            const res = await translateText(toTranslate, langLearn.value, langBase.value, logout, true);
            if (res) {
                setTranslatedText(res);
                // this update is used to trigger the useEffect
                setUpdateTranslateVar(prev => prev + 1);
            }
        } catch (error) {
            console.error('Error during translation:', error);
            if (errorCallBack) { errorCallBack(error); } 
            else {
                setError(error);
            }
        } finally {             
           handleClose(); // Close the dialog after translation
        }
    };

    const getLabel = () => {
        const commonPart = ` or paste text in ${langBase.label}`
        if (speechRecSupported ) {
            return 'Speak, type' + commonPart;
        }
        return 'Type' + commonPart;
    }

    return (
        <Box>

        <Dialog
            open={isModalOpen}
            onClose={handleClose} // Use modified handleClose to ensure cleanup
            aria-labelledby="form-translate-dialog"
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: { 
                    backgroundColor: 'seashell', 
                    borderRadius: 2, 
                    p: 2 
                }
            }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ mb: 1 }}>
                    Translate From <b>{langBase?.label}</b> To <b>{langLearn?.label}</b>
                </Typography>

                <IconButton onClick={handleClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
            <Box>
                { speechRecSupported && (
                <IconButton onClick={handleMicClick} color={listening ? "primary" : "default"}>
                    <MicIcon />
                </IconButton> )
                }   
                <IconButton onClick={() => setToTranslate('')}  sx = {{ml:2}}>
                    <DeleteIcon />
                </IconButton>
            </Box>
                <TextField
                    // label={`Speak, type or paste text in ${langBase.label}`}
                    label = {getLabel()}
                    value={toTranslate}
                    onChange={handleInputChange}
                    multiline
                    rows={4}
                    fullWidth
                    variant="outlined"
                    sx={{ mb: 2 }}
                    
                    slotProps={{
                            input: {
                            dir: isRTL(langBase.label) ? 'rtl' : 'ltr',
                            style: { textAlign: isRTL(langBase.label) ? 'right' : 'left' },
                            }
                        }}
                />
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'flex-start' }}>
                <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleTranslateClick}
                    title="Translated text will be entered into Native Speaker text area"
                >
                    Translate
                </Button>
            </DialogActions>
        </Dialog>
        {error && (
            <ErrorDispatcher 
                error={error}
                showDialog={true}
            />
        )}
        </Box>
        
    );
};

export { FormTranslate, translateText };